import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useSecureAdminAuth } from "@/hooks/useSecureAdminAuth";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Users,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  CreditCard,
  UserCheck,
  RefreshCw,
  Phone,
  Globe,
  Copy,
  Calendar
} from "lucide-react";

/* ---------- types ---------- */
interface UserData {
  id: string;
  email: string;
  full_name: string | null;
  phone_number: string | null;
  country_code: string | null;
  country_name: string | null;
  email_updated: boolean | null;
  created_at: string;

  /* purchase summary */
  has_purchase: boolean;
  purchase_status: string | null;
  purchase_amount: number | null;
  purchase_amount_usd: number;
  purchase_amount_inr: number;
  purchase_currency: string | null;
  purchase_date: string | null;
  start_date: string | null;
  end_date: string | null;
  razorpay_order_id: string | null;
  razorpay_payment_id: string | null;
  razorpay_signature: string | null;
  payment_from: string | null;

  /* gateway provenance tracking */
  is_pay_from_PayGateWay: boolean;        // <-- used in the UI
  original_payment_from: string | null;
  original_purchase_amount: number | null;
  original_is_premium: boolean;
  original_razorpay_order_id: string | null;

  /* admin overrides */
  admin_payment_type: string | null;
  is_user_premium: boolean;
}

const UserManager = () => {
  /* ---------- state ---------- */
  const [users, setUsers] = useState<UserData[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingUser, setUpdatingUser] = useState<string | null>(null);
  const [paymentTypes, setPaymentTypes] = useState<Record<string, string>>({});
  const [premiumPriceINR, setPremiumPriceINR] = useState<number>(299);
  const [premiumPriceUSD, setPremiumPriceUSD] = useState<number>(4);

  const { toast } = useToast();
  const { adminUser } = useSecureAdminAuth();

  /* ---------- helpers ---------- */
  const numberToWords = (num: number): string => {
    if (num === 0) return "Zero";
    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine"
    ];
    const teens = [
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen"
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety"
    ];
    const convertHundreds = (n: number): string => {
      let result = "";
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + " Hundred ";
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + " ";
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + " ";
        return result;
      }
      if (n > 0) {
        result += ones[n] + " ";
      }
      return result;
    };
    let result = "";
    let crores = Math.floor(num / 10000000);
    if (crores > 0) {
      result += convertHundreds(crores) + "Crore ";
      num %= 10000000;
    }
    let lakhs = Math.floor(num / 100000);
    if (lakhs > 0) {
      result += convertHundreds(lakhs) + "Lakh ";
      num %= 100000;
    }
    let thousandsValue = Math.floor(num / 1000);
    if (thousandsValue > 0) {
      result += convertHundreds(thousandsValue) + "Thousand ";
      num %= 1000;
    }
    if (num > 0) result += convertHundreds(num);
    return result.trim();
  };

  const formatDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString() + " " + new Date(d).toLocaleTimeString() : "-";

  const formatPhoneNumber = (p: string | null, c: string | null) =>
    p ? `${c || ""}${p}` : "-";

  const copyToClipboard = (txt: string, label: string) =>
    navigator.clipboard.writeText(txt).then(() =>
      toast({
        title: "Copied!",
        description: `${label} copied to clipboard`
      })
    );

  /* ---------- settings ---------- */
  const fetchPremiumPrice = async () => {
    try {
      // Fetch both INR and USD prices
      const { data, error } = await supabase
        .from("printables_settings")
        .select("setting_key, setting_value")
        .in("setting_key", ["premium_price_inr", "premium_price_usd"]);
      
      if (error) throw error;
      
      data?.forEach((setting) => {
        const price = parseFloat(setting.setting_value);
        if (!isNaN(price)) {
          if (setting.setting_key === "premium_price_inr") {
            setPremiumPriceINR(price);
          } else if (setting.setting_key === "premium_price_usd") {
            setPremiumPriceUSD(price);
          }
        }
      });
    } catch (err) {
      console.error("Error fetching premium prices: ", err);
    }
  };

  /* ---------- saved payment‑type preferences ---------- */
  const savePaymentType = async (userId: string, paymentType: string) => {
    try {
      await supabase
        .from("user_payment_preferences")
        .upsert(
          { user_id: userId, payment_type: paymentType, updated_at: new Date().toISOString() },
          { onConflict: "user_id" }
        );
    } catch (error) {
      console.error("Error saving payment type:", error);
      throw error;
    }
  };

  /* ---------- main fetch ---------- */
  const fetchUsers = async () => {
    try {
      setLoading(true);

      // Ensure admin auth info is available
      if (!adminUser) {
        throw new Error('Admin session not available. Please log in again.');
      }

      // Fetch data through admin endpoint
      const adminSessionToken =
        typeof window !== "undefined"
          ? localStorage.getItem("admin_session_token") || ""
          : "";
      const { data, error } = await supabase.functions.invoke('admin-get-users', {
        headers: { "x-admin-session-token": adminSessionToken },
        body: {
          adminUser: { username: adminUser.username, email: adminUser.email }
        }
      });

      if (error) {
        throw error;
      }

      if (!data || !data.profiles) {
        throw new Error('No profiles data returned from server');
      }

      const allProfiles = data.profiles || [];
      const purchases = data.purchases || [];
      const paymentPrefs = data.paymentPreferences || [];

      /* transform payment preferences to map */
      const savedPaymentTypes: Record<string, string> = {};
      paymentPrefs.forEach((pref: any) => {
        savedPaymentTypes[pref.user_id] = pref.payment_type;
      });
      setPaymentTypes(savedPaymentTypes);

      /* transform per user */
      const transformed: UserData[] = allProfiles.map((profile) => {
        /* all purchases for this user */
        const userPurchases = purchases.filter((p) => p.user_id === profile.id);

        /* newest purchase (could be an admin override) */
        const latestPurchase = userPurchases[0] || null;

        /* ORIGINAL gateway purchase (has razorpay_order_id, any status) */
        const gatewayPurchase = userPurchases.find((p) => Boolean(p.razorpay_payment_id));

        /* derived booleans - Use database is_premium status */
        const isPremium = profile.is_premium || false;

        const isPayFromGateway = Boolean(gatewayPurchase?.razorpay_payment_id); // <‑‑ ALWAYS true if an order id exists

        return {
          id: profile.id,
          email: profile.email || "",
          full_name: profile.full_name,
          phone_number: profile.phone_number,
          country_code: profile.country_code,
          country_name: profile.country_name,
          email_updated: profile.email_updated || false,
          created_at: profile.created_at,

          has_purchase: !!latestPurchase,
          purchase_status: latestPurchase?.status || null,
          purchase_amount: latestPurchase?.amount || null, // Keep for backward compatibility
          purchase_amount_usd: latestPurchase?.amount_usd || 0,
          purchase_amount_inr: latestPurchase?.amount_inr || 0,
          purchase_currency: latestPurchase?.currency || null,
          purchase_date: latestPurchase?.created_at || null,
          start_date: latestPurchase?.start_date || null,
          end_date: latestPurchase?.end_date || null,
          razorpay_order_id: latestPurchase?.razorpay_order_id || null,
          razorpay_payment_id: latestPurchase?.razorpay_payment_id || null,
          razorpay_signature: latestPurchase?.razorpay_signature || null,
          payment_from: latestPurchase?.payment_from || null,

          /* gateway provenance (NEVER changes after it becomes true) */
          is_pay_from_PayGateWay: isPayFromGateway,
          original_payment_from: gatewayPurchase?.payment_from || null,
          original_purchase_amount: gatewayPurchase?.amount || null, // Keep for backward compatibility
          original_purchase_amount_usd: gatewayPurchase?.amount_usd || 0,
          original_purchase_amount_inr: gatewayPurchase?.amount_inr || 0,
          original_is_premium:
            gatewayPurchase ? gatewayPurchase.status === "completed" : false,
          original_razorpay_order_id: gatewayPurchase?.razorpay_order_id || null,

          /* admin overrides / display helpers */
          admin_payment_type: savedPaymentTypes[profile.id] || null,
          is_user_premium: isPremium
        };
      });

      setUsers(transformed);
      setFilteredUsers(transformed);
      // Removed success toast to avoid spam
    } catch (err: any) {
      console.error("fetchUsers error: ", err);
      toast({
        title: "Error",
        description: err.message || "Failed to load users",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  /* ---------- admin actions ---------- */
  const handlePaymentStatusToggle = async (user: UserData, isCompleted: boolean) => {
    try {
      setUpdatingUser(user.id);

      // Update profiles.is_premium in database
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ is_premium: isCompleted })
        .eq('id', user.id);

      if (profileError) {
        throw profileError;
      }

      // Forward admin session token so the edge function can verify privileged caller
      const adminSessionToken =
        typeof window !== "undefined"
          ? localStorage.getItem("admin_session_token") || ""
          : "";
      const adminHeaders = { "x-admin-session-token": adminSessionToken };

      // If user has razorpay_payment_id, keep original payment data
      if (user.razorpay_payment_id) {
        await supabase.functions.invoke("admin-update-payment-status", {
          headers: adminHeaders,
          body: {
            userId: user.id,
            userEmail: user.email,
            isCompleted,
            adminAction: true,
            paymentType: "online",
            amount: user.purchase_amount || 0, // Keep original amount
            preserveOriginalData: true
          }
        });
      } else {
        // For users without razorpay_payment_id
        if (isCompleted) {
          // Turning ON premium - need payment type selection
          const paymentType = paymentTypes[user.id] || user.admin_payment_type || "no-payment";
          
          let amount = 0;
          let amountINR = 0;
          let amountUSD = 0;
          
          if (paymentType === "offline") {
            // Use appropriate currency based on user's country
            const isIndian = user.country_code === "+91";
            if (isIndian) {
              amountINR = premiumPriceINR;
              amount = premiumPriceINR;
            } else {
              amountUSD = premiumPriceUSD;
              amount = premiumPriceUSD;
            }
          }
          // For "no-payment" or "online" manual selection, amounts stay 0
          
          await supabase.functions.invoke("admin-update-payment-status", {
            headers: adminHeaders,
            body: {
              userId: user.id,
              userEmail: user.email,
              isCompleted: true,
              adminAction: true,
              paymentType,
              amount,
              amountINR,
              amountUSD,
              preserveOriginalData: user.is_pay_from_PayGateWay
            }
          });
        } else {
          // Turning OFF premium - only change premium status, keep other fields
          await supabase.functions.invoke("admin-update-payment-status", {
            headers: adminHeaders,
            body: {
              userId: user.id,
              userEmail: user.email,
              isCompleted: false,
              adminAction: true,
              paymentType: paymentTypes[user.id] || user.admin_payment_type || "no-payment",
              amount: 0, // Don't change amounts when turning off
              preserveOriginalData: user.is_pay_from_PayGateWay,
              onlyTogglePremium: true // Special flag for this case
            }
          });
        }
      }

      await fetchUsers();
      // Removed success toast for payment status changes
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error",
        description: err.message || "Failed to update payment status",
        variant: "destructive"
      });
    } finally {
      setUpdatingUser(null);
    }
  };

  const handlePaymentTypeChange = async (userId: string, paymentType: string) => {
    try {
      setPaymentTypes((prev) => ({ ...prev, [userId]: paymentType }));
      await savePaymentType(userId, paymentType);
      await fetchUsers();
      // Removed success toast for payment type changes
    } catch (err: any) {
      setPaymentTypes((prev) => {
        const copy = { ...prev };
        delete copy[userId];
        return copy;
      });
      toast({
        title: "Error",
        description: err.message || "Failed to update payment type",
        variant: "destructive"
      });
    }
  };

  /* ---------- derived stats ---------- */
  const totalUsers = users.length;
  const paidUsers = users.filter((u) => u.has_purchase && u.purchase_status === "completed")
    .length;
  const pendingUsers = users.filter((u) => u.has_purchase && u.purchase_status === "pending")
    .length;

  const getTotalRevenue = () => {
    let inrTotal = 0;
    let usdTotal = 0;

    users.forEach(u => {
      if (u.has_purchase && u.purchase_status === "completed") {
        // Use the new separate amount columns
        inrTotal += u.purchase_amount_inr || 0;
        usdTotal += u.purchase_amount_usd || 0;
      }
    });

    return { inrTotal, usdTotal };
  };

  const { inrTotal, usdTotal } = getTotalRevenue();


  /* ---------- effects ---------- */
  useEffect(() => {
    (async () => {
      await fetchPremiumPrice();
      if (adminUser) {
        await fetchUsers();
      }
    })();
  }, [adminUser]);

  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    setFilteredUsers(
      users.filter(
        (u) =>
          u.email.toLowerCase().includes(lower) ||
          u.full_name?.toLowerCase().includes(lower) ||
          u.phone_number?.includes(searchTerm) ||
          u.country_name?.toLowerCase().includes(lower) ||
          u.id.toLowerCase().includes(lower)
      )
    );
  }, [searchTerm, users]);

  /* ---------- render helpers ---------- */
  const getRegistrationBadge = () => (
    <Badge variant="default" className="bg-blue-500">
      <UserCheck className="w-3 h-3 mr-1" />
      Registered
    </Badge>
  );

  const formatAmountINR = (u: UserData) => {
    return u.purchase_amount_inr > 0 ? `₹${u.purchase_amount_inr.toFixed(2)}` : "-";
  };

  const formatAmountUSD = (u: UserData) => {
    return u.purchase_amount_usd > 0 ? `$${u.purchase_amount_usd.toFixed(2)}` : "-";
  };

  const getStatusBadge = (u: UserData) => {
    if (!u.has_purchase) return <Badge variant="secondary">No Purchase</Badge>;
    const isAdmin = u.payment_from === "by admin";
    switch (u.purchase_status) {
      case "completed":
        return (
          <Badge variant="default" className={isAdmin ? "bg-purple-500" : "bg-green-500"}>
            {isAdmin ? "Admin Activated" : "Verified"}
          </Badge>
        );
      case "pending":
        return <Badge variant="default" className="bg-yellow-500">Pending</Badge>;
      case "failed":
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  /* ---------- UI ---------- */
  return (
    <div className="space-y-6">
      <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
      {/* header / refresh */}
      <div className="flex items-center justify-between">
  <div>
    <h2 className="text-3xl font-bold text-slate-800 inline-block mr-4">User Management</h2>
    <Button
      onClick={() => {
        fetchPremiumPrice();
        fetchUsers();
      }}
      disabled={loading}
      className={`flex items-center gap-2 ml-0`}
    >
      <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
      Refresh
    </Button>
    <p className="text-slate-600 mt-1">Manage registered users and payment status</p>
  </div>
</div>


      {/* stats */}
      <div className="grid grid-cols-1 md:grid-cols-10 gap-5">
        {/* Total users */}
        <Card className="p-3 border border-red-500">
          <CardHeader className="flex flex-row items-center justify-between pb-1">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="p-0">
            <div className="text-lg font-bold">{totalUsers}</div>
            <p className="text-xs text-muted-foreground">Registered</p>
          </CardContent>
        </Card>

        {/* Paid */}
        <Card className="p-3 border border-red-500">
          <CardHeader className="flex flex-row items-center justify-between pb-1">
            <CardTitle className="text-sm font-medium">Paid Users</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent className="p-0">
            <div className="text-lg font-bold text-green-600">{paidUsers}</div>
            <p className="text-xs text-muted-foreground">Verified</p>
          </CardContent>
        </Card>

        {/* Pending */}
        <Card className="p-3 border border-red-500">
          <CardHeader className="flex flex-row items-center justify-between pb-1">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent className="p-0">
            <div className="text-lg font-bold text-yellow-600">{pendingUsers}</div>
            <p className="text-xs text-muted-foreground">Awaiting</p>
          </CardContent>
        </Card>

        {/* Revenue INR */}
        <Card className="p-3 border border-red-500">
          <CardHeader className="flex flex-row items-center justify-between pb-1">
            <CardTitle className="text-sm font-medium">Revenue ₹</CardTitle>
            <CreditCard className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent className="p-0">
            <div className="text-lg font-bold text-blue-600">
              ₹{inrTotal.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">INR Total</p>
          </CardContent>
        </Card>

        {/* Revenue USD */}
        <Card className="p-3 border border-red-500">
          <CardHeader className="flex flex-row items-center justify-between pb-1">
            <CardTitle className="text-sm font-medium">Revenue $</CardTitle>
            <CreditCard className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent className="p-0">
            <div className="text-lg font-bold text-green-600">
              ${usdTotal.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">USD Total</p>
          </CardContent>
        </Card>
      </div>

      {/* search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2">
            <Search className="w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by email, name, phone, country, user ID…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-lg"
            />
          </div>
        </CardContent>
      </Card>

      {/* table */}
      <Card>
        <CardHeader>
          <CardTitle>Users List ({filteredUsers.length} users)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[120px]">User ID</TableHead>
                    <TableHead className="min-w-[200px]">Customer</TableHead>
                    <TableHead className="min-w-[150px]">Contact</TableHead>
                    <TableHead className="min-w-[100px]">Country</TableHead>
                    <TableHead className="min-w-[120px]">Email Updated</TableHead>
                    <TableHead className="min-w-[150px]">Account&nbsp;Created</TableHead>
                    <TableHead className="min-w-[120px]">Admin Control</TableHead>
                    <TableHead className="min-w-[150px]">Payment Type</TableHead>
                     <TableHead className="min-w-[80px]">Amount ₹</TableHead>
                     <TableHead className="min-w-[80px]">Amount $</TableHead>
                    <TableHead className="min-w-[150px]">Start</TableHead>
                    <TableHead className="min-w-[150px]">End</TableHead>
                    <TableHead className="min-w-[150px]">Payment&nbsp;Date</TableHead>
                    <TableHead className="min-w-[180px]">Payment ID</TableHead>
                    <TableHead className="min-w-[200px]">Razorpay&nbsp;Order&nbsp;ID</TableHead>
                    <TableHead className="min-w-[120px]">Is&nbsp;Premium</TableHead>
                    <TableHead className="min-w-[150px]">Is&nbsp;Pay&nbsp;from&nbsp;Gateway</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {filteredUsers.map((u) => (
                    <TableRow key={u.id}>
                      {/* user id */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                            {u.id.slice(0, 8)}…
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(u.id, "User ID")}
                            className="h-6 w-6 p-0"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>

                      {/* customer */}
                      <TableCell>
                        <div>
                          <div className="font-medium">{u.full_name || "No name"}</div>
                          <div className="text-sm text-gray-500 flex items-center gap-2">
                            {u.email}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(u.email, "Email")}
                              className="h-4 w-4 p-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </TableCell>

                      {/* contact */}
                      <TableCell>
                        {u.phone_number ? (
                          <div className="flex items-center gap-1 text-sm">
                            <Phone className="w-3 h-3" />
                            <span>{formatPhoneNumber(u.phone_number, u.country_code)}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                copyToClipboard(
                                  formatPhoneNumber(u.phone_number, u.country_code),
                                  "Phone"
                                )
                              }
                              className="h-4 w-4 p-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">No phone</span>
                        )}
                      </TableCell>

                      {/* country */}
                      <TableCell>
                        {u.country_name ? (
                          <div className="flex items-center gap-1 text-sm">
                            <Globe className="w-3 h-3" />
                            <span>{u.country_name}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>

                      {/* email updated */}
                      <TableCell>
                        <Badge 
                          variant={u.email_updated ? "default" : "secondary"}
                          className={u.email_updated ? "bg-green-500" : "bg-gray-400"}
                        >
                          {u.email_updated ? "Yes" : "No"}
                        </Badge>
                      </TableCell>

                      {/* created */}
                      <TableCell>
                        {getRegistrationBadge()}
                        <div className="text-xs text-gray-500">{formatDate(u.created_at)}</div>
                      </TableCell>

                      {/* admin control */}
                      <TableCell>
                        <div className="flex items-center space-x-2">
                           <Switch
                            checked={u.is_user_premium}
                            onCheckedChange={(c) => handlePaymentStatusToggle(u, c)}
                            disabled={updatingUser === u.id}
                          />
                          <span className="text-sm text-gray-600">
                            {u.is_user_premium ? "Active" : "Inactive"}
                          </span>
                          {updatingUser === u.id && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          )}
                        </div>
                      </TableCell>

                      {/* payment type radio */}
                      <TableCell>
                        <RadioGroup
                          value={
                            u.razorpay_payment_id 
                              ? "online" 
                              : paymentTypes[u.id] || u.admin_payment_type || "no-payment"
                          }
                          onValueChange={(v) => handlePaymentTypeChange(u.id, v)}
                          className="space-y-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="offline" id={`off-${u.id}`} />
                            <Label htmlFor={`off-${u.id}`} className="text-xs">
                              Offline Payment
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="online" id={`on-${u.id}`} />
                            <Label htmlFor={`on-${u.id}`} className="text-xs">
                              Online
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="no-payment" id={`nop-${u.id}`} />
                            <Label htmlFor={`nop-${u.id}`} className="text-xs">
                              No Payment
                            </Label>
                          </div>
                        </RadioGroup>
                      </TableCell>

                       {/* amount INR */}
                       <TableCell className="font-medium">{formatAmountINR(u)}</TableCell>
                       
                       {/* amount USD */}
                       <TableCell className="font-medium">{formatAmountUSD(u)}</TableCell>

                      {/* dates */}
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(u.start_date)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div
                          className={`flex items-center gap-1 text-sm ${
                            u.end_date && new Date(u.end_date) < new Date()
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(u.end_date)}</span>
                        </div>
                      </TableCell>

                      {/* payment date */}
                      <TableCell className="text-sm">{formatDate(u.purchase_date)}</TableCell>

                      {/* payment id */}
                      <TableCell>
                        {u.razorpay_payment_id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono bg-blue-100 px-2 py-1 rounded">
                              {u.razorpay_payment_id.slice(0, 15)}…
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                copyToClipboard(u.razorpay_payment_id || "", "Payment ID")
                              }
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>

                      {/* order id */}
                      <TableCell>
                        {u.razorpay_order_id ? (
                          <div className="flex items-center gap-2">
                            <span className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                              {u.razorpay_order_id.slice(0, 20)}…
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                copyToClipboard(u.razorpay_order_id || "", "Order ID")
                              }
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>

                      {/* premium */}
                      <TableCell>
                        <Badge
                          variant={u.is_user_premium ? "default" : "secondary"}
                          className={u.is_user_premium ? "bg-green-500" : "bg-gray-400"}
                        >
                          {u.is_user_premium ? "Yes" : "No"}
                        </Badge>
                      </TableCell>

                      {/* gateway flag (NEVER changes once true) */}
                      <TableCell>
                        <Badge
                          variant={u.is_pay_from_PayGateWay ? "default" : "secondary"}
                          className={u.is_pay_from_PayGateWay ? "bg-blue-500" : "bg-gray-400"}
                        >
                          {u.is_pay_from_PayGateWay ? "True" : "False"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {filteredUsers.length === 0 && !loading && (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? "No users match your search." : "No users yet."}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManager;
